import { StorageManager } from '@aws-amplify/ui-react-storage';
import MaterialTable, { MTableAction } from '@material-table/core';
import { DeleteOutline, InfoOutlined } from '@mui/icons-material';
import { Box, Button, Card, CardContent, Divider, InputAdornment, Tab, Tabs, TextField, Tooltip, Typography } from '@mui/material';
import { generateClient, post } from 'aws-amplify/api';
import { enqueueSnackbar } from 'notistack';
import { forwardRef, useContext, useEffect, useRef, useState } from 'react';
import { UserContext } from '../../contexts/UserContext';

import { fetchAuthSession } from 'aws-amplify/auth';
import { getUrl } from 'aws-amplify/storage';
import { useParams } from 'react-router-dom';
import { ConfirmDialog } from '../../components/confirmDialog/confirmDialog';
import { PageAppBar } from '../../components/pageAppBar';
import { createContactFieldSuppression, deleteContactFieldSuppression } from '../../graphql/mutations';
import { contactFieldSuppressionByList, getContactFieldSuppressionList } from '../../graphql/queries';
import { NotificationGroupSelect } from '../../components/notifications/NotificationGroupSelect';
import { actionOneButtonStyle, actionTwoButtonStyle, cardStyle, tabStyle } from '../../theme';

export function ContactFieldSuppression() {
	const client = generateClient();
	const userContext = useContext(UserContext);
	const { id } = useParams();
	const [selSuppressionList, setSelSuppressionList] = useState({});
	const [loading, setLoading] = useState(false);
	const [delimiter, setDelimiter] = useState(';');
	const [currentTab, setCurrentTab] = useState(0);
	const [deleting, setDeleting] = useState(false);
	const [adding, setAdding] = useState(false);
	const [confirmDeleteSelection, setConfirmDeleteSelection] = useState(false);
	const [selectionToDelete, setSelectionToDelete] = useState([]);
	const [uploading, setUploading] = useState(false);
	const [notificationGroup, setNotificationGroup] = useState(null);
	const multiBoxRef = useRef(null);
	const valueTableRef = useRef();
	const [newValueTrigger, setNewValueTrigger] = useState(false);

	const [searchQuery, setSearchQuery] = useState('');
	const [nextToken, setNextToken] = useState(null);
	const [currentPage, setCurrentPage] = useState(0);
	const [pageTokens, setPageTokens] = useState({});

	useEffect(() => {
		async function getData() {
			searchValues({ page: 0, pageSize: 10 });
			// Get the list
			const result = await client.graphql({
				query: getContactFieldSuppressionList,
				variables: { id, tenant: userContext.tenantId }
			});
			setSelSuppressionList(result.data.getContactFieldSuppressionList);
		}
		if (id && userContext.tenantId) {
			getData();
		}

	}, [id, userContext.tenantId]);

	const searchValues = async (query) => {
		let currentPageTokens = {
			...pageTokens,
		};

		const params = {
			contactFieldSuppressionList: id,
		};

		if (query.search) {
			setSearchQuery(query.search);
			params.contactFieldValue = { 'beginsWith': query.search };
		}

		if (query.page > currentPage) {
			params.nextToken = nextToken;
			currentPageTokens[query.page] = nextToken;
		} else {
			if (query.search === searchQuery) {
				params.nextToken = currentPageTokens[query.page];
			} else {
				params.nextToken = null;
				currentPageTokens = {
					0: null,
				};
				setCurrentPage(0);
			}
		}

		if (query.pageSize) {
			params.limit = query.pageSize;
		}

		if (query.orderBy) {
			let sortField = `${query.orderBy.field}.keyword`;
			params.sort = [{ field: sortField, order: query.orderDirection }];
		}

		const result = await client.graphql({
			query: contactFieldSuppressionByList,
			variables: params
		});

		if (result.data.contactFieldSuppressionByList !== null) {
			setNextToken(result.data.contactFieldSuppressionByList.nextToken);
			setCurrentPage(query.page);
			setPageTokens(currentPageTokens);

			return {
				data: result.data.contactFieldSuppressionByList.items,
				page: query.page
			};
		} else {
			return {
				data: [],
				page: query.page
			};
		}
	};


	function getMultiValues() {
		let separator = delimiter.trim();
		if (separator === '\\r\\n') {
			separator = '\n';
		}

		const valueStrings = multiBoxRef.current.split(separator);


		const values = [];
		const strings = [];

		for (let valueString of valueStrings) {
			valueString = valueString.trim();
			values.push(valueString);
			strings.push(valueString);
		}

		multiBoxRef.current = strings.join(separator);

		return [true, values];
	}

	function formatData() {
		let values = [];

		if (delimiter.trim() === '') {
			enqueueSnackbar("A delimiter is required", { variant: 'error' });
			return [false, []];
		}

		if (multiBoxRef.current.trim() === '') {
			enqueueSnackbar("Please provide at least one value", { variant: 'info' });
			return [false, []];
		}

		const result = getMultiValues();
		values = result[1];

		if (!result[0]) {
			enqueueSnackbar("Invalid value found.", { variant: 'error' });
			return [false, []];
		}


		if (values.length === 0) {
			enqueueSnackbar("No values to process", { variant: 'info' });
			return [false, []];
		}

		return [true, values];
	}

	async function handleAdd() {
		setAdding(true);
		const result = formatData();
		if (result[0]) {
			await post({
				apiName: 'cdyxoutreach',
				path: '/dnc/field-suppression',
				options: {
					headers: {
						Authorization: `Bearer ${(await fetchAuthSession()).tokens.idToken}`,
						'x-api-key': userContext.apiKey
					},
					body: {
						tenant: userContext.tenantId,
						values: result[1],
						listId: selSuppressionList.id
					}
				}
			}).response;
			searchValues({ page: 0, pageSize: 10 });
		}
		setAdding(false);
		setCurrentTab(0);
	}

	async function handleRemove() {
		setDeleting(true);
		const result = formatData();
		if (result[0]) {
			const response = await unsafeDeleteListOfValues(result[1]);
			if (typeof (response) === 'object' && "values" in (response ?? {})) {
				enqueueSnackbar("Some values were unable to be deleted", { variant: 'warning' });
			}

			searchValues({ page: 0, pageSize: 10 });
		}
		setDeleting(false);
	}

	async function unsafeDeleteListOfValues(values) {
		return await post({
			apiName: 'cdyxoutreach',
			path: '/dnc/field-suppression/delete',
			options: {
				headers: {
					Authorization: `Bearer ${(await fetchAuthSession()).tokens.idToken}`,
					'x-api-key': userContext.apiKey
				},
				body: {
					tenant: userContext.tenantId,
					values: values,
					listId: selSuppressionList.id
				}
			}
		}).response;
	}

	async function handleUpload(path) {
		try {
			const urlObject = await getUrl({
				path,
				options: {
					level: 'private',
				},
			});

			const url = urlObject.url.toString();
			const session = await fetchAuthSession();

			await post({
				apiName: 'cdyxoutreach',
				path: '/dnc/field-suppression/import',
				options: {
					headers: {
						Authorization: `Bearer ${session.tokens.idToken}`,
						'x-api-key': userContext.apiKey,
					},
					body: {
						tenant: userContext.tenantId,
						url,
						email: session.tokens.idToken.payload.email,
						listId: selSuppressionList.id,
						notificationGroup: notificationGroup ? notificationGroup : null,
						jwtToken: session.tokens.idToken.toString()
					}
				}
			}).response;

			const notifyMsg = notificationGroup
				? `File uploaded successfully. The "${notificationGroup.name}" group will be notified upon import completion.`
				: 'File uploaded successfully. You will receive a notification upon import completion.';
			enqueueSnackbar(notifyMsg, { variant: 'success' });
			setCurrentTab(0);
		} catch (err) {
			console.log(err);
			enqueueSnackbar("There was an error uploading and importing the file.", { variant: 'error' });
		}

		setUploading(false);
	}

	async function handleConfirmDeleteSelection(data) {
		setLoading(true);
		setConfirmDeleteSelection(false);
		setDeleting(true);
		await unsafeDeleteListOfValues(data.map(x => x.contactFieldValue));
		setDeleting(false);
		setLoading(false);
		searchValues({ page: 0, pageSize: 10 });
	}

	function handleAddNewValue() {
		valueTableRef.current.state.showAddRow = true;
		setNewValueTrigger(!newValueTrigger);
	}

	return (
		<Box>
			<PageAppBar
				title={selSuppressionList?.name ?? 'Contact Field Suppression'}
				description={`Contact's will be suppressed from this list if their ${selSuppressionList?.contactField} field
				 matches the value`}
				actionOneText={currentTab === 0 ? '+ Value' : ''}
				actionOneHandler={currentTab === 0 ? handleAddNewValue : undefined}
				actionOneDisabled={userContext.securityOptions.suppression < 2}
				actionTwoText='Close'
				actionTwoHandler={() => window.history.back()}
			/>
			<Card variant='outlined' sx={cardStyle}>
				<CardContent>
					<Box>
						<Box>
							<Tabs value={currentTab} onChange={(_, newTab) => setCurrentTab(newTab)} style={{ marginBottom: '10px' }}>
								<Tab sx={tabStyle} label="Suppressed Values" id={0} />
								<Tab sx={tabStyle} label='Upload Values' id={1} />
								<Tab sx={tabStyle} label='Quick Add/Remove' id={2} />
							</Tabs>
						</Box>
						<Divider />
						<Box>
							{currentTab === 2 &&
								<Box>
									<Box>
										<TextField
											label="Suppression Values"
											multiline
											rows={4}
											color='primary'
											value={multiBoxRef.current}
											onChange={e => multiBoxRef.current = e.target.value}
											sx={{ width: '100%', marginTop: '10px' }}
										/>
									</Box>
									<Box>
										<Box>
											<TextField
												label='Delimiter'
												variant='standard'
												color='primary'
												value={delimiter}
												onChange={e => setDelimiter(e.target.value)}
												error={delimiter.trim() === ''}
												helperText={delimiter.trim() === '' && <>A delimiter is required</>}
												InputProps={{
													endAdornment: (
														<InputAdornment position='end'>
															<Tooltip title='Type \r\n to designate a newline as the delimiter'>
																<InfoOutlined color='primary' />
															</Tooltip>
														</InputAdornment>
													)
												}} />
										</Box>
									</Box>
									<Box display="flex">
										<Box >
											<Button sx={actionOneButtonStyle} onClick={handleAdd} disabled={adding || deleting || loading || !selSuppressionList}>Add</Button>
										</Box>
										<Box item>
											<Button sx={actionTwoButtonStyle} onClick={handleRemove} disabled={adding || deleting || loading || !selSuppressionList}>Remove</Button>
										</Box>
									</Box>
								</Box>}
							{currentTab === 1 &&
								<Box>
									<Box display='flex' gap={1} alignItems='center'>
										<InfoOutlined color='primary' />
										<Typography variant='body1'>Any column headers containing the word <i>phone</i> will be added</Typography>
									</Box>
									<NotificationGroupSelect
										value={notificationGroup}
										onChange={(group) => setNotificationGroup(group)}
									/>
									<Box>
										<StorageManager
											displayText={{
												dropFilesText: 'Drop file here or'
											}}
											acceptedFileTypes={['.csv']}
											path={({ identityId }) => `private/${identityId}/`}
											maxFileCount={1}
											autoUpload={false}
											onUploadSuccess={(file) => {
												handleUpload(file.key);
											}

											}
										/>
									</Box>
								</Box>}
							{currentTab === 0 &&
								<Box>
									<Box item container direction="row" spacing={2}>
										<Box item style={{ flexGrow: 1, height: '100%' }}>
											<MaterialTable
												tableRef={valueTableRef}
												title=""
												isLoading={loading}
												columns={[
													{ title: 'Value', field: 'contactFieldValue' },
													{ title: 'Created', field: 'createdAt', render: rowData => rowData?.createdAt ? (new Date(rowData.createdAt)).toLocaleString() : (new Date()).toLocaleString(), editable: 'never' }
												]}
												data={searchValues}
												options={{
													selection: true,
													showSelectAllCheckbox: true,
													actionsColumnIndex: -1,
													pageSize: 10,
													padding: 'dense',
													addRowPosition: 'first',
													searchFieldStyle: {
														marginBottom: '16px',
														marginleft: '-28px',
													},
													headerStyle: {
														borderTop: '1px solid #e0e0e0',
														padding: '16px',
													},
													searchAutoFocus: true,
													searchFieldVariant: 'outlined',
													paginationType: 'stepped',
												}}
												components={{
													Action: props => {
														if (props.action.tooltip !== "Add") return <MTableAction {...props} />;
														else return null;
													},
													Container: (props) => <Box {...props} elevation={0} />,
												}}
												icons={{
													Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} color='primary' />),
												}}
												actions={[
													{
														disabled: !selSuppressionList || userContext.securityOptions.suppression < 2,
														tooltip: 'Remove All Selected Values',
														icon: 'delete',
														onClick: async (_, data) => {
															setSelectionToDelete(data);
															setConfirmDeleteSelection(true);
														}
													}
												]}
												editable={{
													isEditable: selSuppressionList === null || userContext.securityOptions.suppression > 1,
													onRowAdd: newData => new Promise(async (resolve, reject) => {
														if (userContext.securityOptions.suppression < 2) {
															reject();
														}
														try {
															await client.graphql({
																query: createContactFieldSuppression,
																variables: {
																	input: {
																		contactFieldSuppressionList: selSuppressionList.id,
																		contactFieldValue: newData.contactFieldValue,
																		tenant: userContext.tenantId
																	}
																}
															})

															searchValues({ page: 0, pageSize: 10 });
															resolve();
														} catch (err) {
															console.log(err);
															reject();
														}
													}),
													onRowDelete: toDelete => new Promise(async (resolve, reject) => {
														if (userContext.securityOptions.suppression < 2) {
															reject();
														}
														try {
															await client.graphql({
																query: deleteContactFieldSuppression,
																variables: {
																	input: {
																		id: toDelete.id
																	}
																}
															});

															enqueueSnackbar("Value deleted", { variant: 'success' });
															resolve();
														} catch (err) {
															console.log(err);
															enqueueSnackbar("Unable to delete value", { variant: 'error' });
															reject();
														}
													})
												}}
											/>
										</Box>
									</Box>
								</Box>}
						</Box>
					</Box>
				</CardContent>
			</Card>
			<ConfirmDialog
				open={confirmDeleteSelection}
				title="Delete Selection"
				description="Are you sure you want to delete this selection of values?"
				actionOneText="Confirm"
				actionOneHandler={() => handleConfirmDeleteSelection(selectionToDelete)}
				actionTwoText="Cancel"
				actionTwoHandler={() => setConfirmDeleteSelection(false)} />
		</Box >
	);
}
